import React from "react";
import Icon from "../../../components/AppIcon";

const ExperienceSummary = () => {
  const experiences = [
    {
      id: 1,
      company: "フリーランス",
      position: "FileMaker 開発者",
      duration: "1998 - 現在",
      location: "岡山県岡山市",
      type: "",
      description:
        "FileMaker Proを活用したカスタムアプリケーションの開発。FileMaker Cloud利用により、クラウドベースのソリューションを提供。",
      achievements: [
        "FileMaker Cloudを利用したクラウドベースのデータベースソリューションを提供",
        "既存FileMakerシステムのiPadでの運用システムへの移行を実施",
        "同窓会管理システムの仕様構築から開発、導入、運用までを一貫して担当",
      ],
      technologies: ["FileMaker Pro", "FileMaker Server", "FileMaker Cloud"],
      logo: "https://portfolio.cue-lock.jp/image-folder/history/A_woman_in_her_40s_wearing_headphones_sitting.png",
    },
    {
      id: 2,
      company: "印刷会社",
      position: "FileMaker 開発者",
      duration: "1996 - 1998",
      location: "岡山県岡山市",
      type: "",
      description:
        "FileMaker Proを活用したカスタムアプリケーションの開発。コード品質・パフォーマンス向上のためのベストプラクティスの導入。",
      achievements: [
        "FileMakerランタイムを利用して、CD-ROMだけで動作するデータベースを制作",
        "既存FileMakerシステムを最新バージョンでリプレイス",
        "学生管理・学生証発行システムの仕様構築から開発、導入、運用までを一貫して担当",
      ],
      technologies: ["FileMaker Pro", "FileMaker Server", "FileMaker Runtime"],
      logo: "https://portfolio.cue-lock.jp/image-folder/history/close-up_zoom_of_paper_documents.png",
    },
  ];

  const stats = [
    { label: "Years of Experience", value: "20+", icon: "Calendar" },
    { label: "Projects Completed", value: "30+", icon: "CheckCircle" },
    { label: "Technologies Mastered", value: "20+", icon: "Code" },
    { label: "Client Satisfaction", value: "98%", icon: "Star" },
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            <Icon name="Briefcase" size={16} className="mr-2" />
            Professional Journey
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            職務経歴
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            多様なプロジェクトやチームにおいて、高品質なソリューションを提供し、技術的卓越性を推進してきた実績があります。
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats?.map((stat, index) => (
            <div
              key={stat?.label}
              className="bg-card rounded-xl p-6 text-center shadow-elevation-sm hover:shadow-elevation-md transition-layout hover-lift"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={stat?.icon} size={24} className="text-primary" />
              </div>
              <div className="text-2xl font-bold text-primary mb-1">
                {stat?.value}
              </div>
              <div className="text-sm text-text-secondary">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Experience Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block"></div>

          <div className="space-y-12">
            {experiences?.map((exp, index) => (
              <div
                key={exp?.id}
                className="relative flex flex-col md:flex-row gap-6"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                {/* Timeline Dot */}
                <div className="hidden md:flex absolute left-6 w-4 h-4 bg-primary rounded-full border-4 border-background shadow-elevation-sm"></div>

                {/* Company Logo */}
                <div className="flex-shrink-0 md:ml-16">
                  <div className="w-16 h-16 rounded-xl overflow-hidden shadow-elevation-sm">
                    <img
                      src={exp?.logo}
                      alt={`${exp?.company} logo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Experience Content */}
                <div className="flex-1 bg-card rounded-xl p-6 shadow-elevation-sm hover:shadow-elevation-md transition-layout">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-1">
                        {exp?.position}
                      </h3>
                      <div className="flex items-center space-x-2 text-primary font-medium mb-2">
                        <Icon name="Building" size={16} />
                        <span>{exp?.company}</span>
                      </div>
                    </div>

                    <div className="flex flex-col lg:items-end space-y-1">
                      <div className="flex items-center text-text-secondary text-sm">
                        <Icon name="Calendar" size={14} className="mr-1" />
                        {exp?.duration}
                      </div>
                      <div className="flex items-center text-text-secondary text-sm">
                        <Icon name="MapPin" size={14} className="mr-1" />
                        {exp?.location}
                      </div>
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                        {exp?.type}
                      </span>
                    </div>
                  </div>

                  <p className="text-text-secondary mb-4">{exp?.description}</p>

                  {/* Key Achievements */}
                  <div className="mb-4">
                    <h4 className="font-medium text-foreground mb-2">
                      達成ポイント：
                    </h4>
                    <ul className="space-y-1">
                      {exp?.achievements?.map((achievement, idx) => (
                        <li
                          key={idx}
                          className="flex items-start text-sm text-text-secondary"
                        >
                          <Icon
                            name="CheckCircle"
                            size={14}
                            className="text-success mr-2 mt-0.5 flex-shrink-0"
                          />
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div>
                    <h4 className="font-medium text-foreground mb-2">
                      使用技術：
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {exp?.technologies?.map((tech) => (
                        <span
                          key={tech}
                          className="px-2 py-1 bg-muted text-text-secondary rounded text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Download Resume CTA */}
        {/* <div className="text-center mt-16">
          <div className="bg-card rounded-2xl p-8 shadow-elevation-sm">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Want to know more about my experience?
                </h3>
                <p className="text-text-secondary">
                  Download my complete resume with detailed project information
                  and references
                </p>
              </div>

              <button className="flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-micro hover-lift">
                <Icon name="Download" size={18} />
                <span>Download Resume</span>
              </button>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default ExperienceSummary;
